import { useContext } from 'react'
import { DesktopCapturerSourceExt, UserContext, UserContextType } from '../UserContext'
import ElectronIPC, { ObsLaunchResult } from './ElectronIPC'
import { env } from './EnvironmentVariables'

type OBSConfigOverrideItem = {
    configName: string
    sectionName: string
    propertyName: string
    type: string
    value: string | number
}

type OBSLaunchParamters = {
    minimizeToTray?: boolean
    configRoot?: string
    cwd?: string

    configOverride?: OBSConfigOverrideItem[]
    runBatch?: string
    startVirtualCam?: boolean
}

export default function useObsController({ instanceName }: { instanceName: 'screenrecord' | 'screenshare' }) {
    const { state, dispatch } = useContext(UserContext) as UserContextType

    async function launchProcess(params: OBSLaunchParamters): Promise<ObsLaunchResult> {
        const result: ObsLaunchResult = await ElectronIPC.ObsInterface(instanceName, 'launchProcess', params)
        console.log(`useObsController: launchProcess() returned %O`, result)
        return result
    }

    function killProcess() {
        ElectronIPC.ObsInterface(instanceName, 'killProcess', [])
    }

    async function isAvailable() {
        // const b = await ElectronIPC.ObsInterface('isAvailable', null)
        // console.log(`useObsController: isAvailable() returned %O`, b)
        // return b
        return true
    }

    function makeConfig({
        screenIndex,
        frameRate,
        baseCanvasDim,
        videoOutputDim,
    }: {
        screenIndex: number
        frameRate: number
        baseCanvasDim?: [number, number] | null
        videoOutputDim?: [number, number] | null
    }) {
        const WIDTH = 0
        const HEIGHT = 1

        let configOverride = [
            { configName: 'globalConfig', sectionName: 'General', propertyName: 'FirstRun', type: 'bool', value: 1 },
            {
                configName: 'globalConfig',
                sectionName: 'General',
                propertyName: 'ConfirmOnExit',
                type: 'bool',
                value: 0,
            },
            { configName: 'basicConfig', sectionName: 'Video', propertyName: 'FPSType', type: 'uint', value: 1 },
            {
                configName: 'basicConfig',
                sectionName: 'Video',
                propertyName: 'FPSInt',
                type: 'uint',
                value: frameRate || 30,
            },
            {
                configName: 'basicConfig',
                sectionName: 'SimpleOutput',
                propertyName: 'DisplayIndex',
                type: 'uint',
                value: screenIndex || 0,
            },
        ]

        {
            let SysTrayWhenStarted = 0
            let SysTrayEnabled = 0
            let HideWindowOnStartOverride = 0
            let NeverShowWindow = 0
            let NoTrayMenu = 0

            if (env.OBS_DEBUG) {
                SysTrayWhenStarted = 1
                SysTrayEnabled = 1
                HideWindowOnStartOverride = 0
                NeverShowWindow = 0
                NoTrayMenu = 0
            } else {
                // On Mac, we _want_ to show the system tray icon. On mac, if we hide the system tray icon,
                // we instead get the bottom tray icon.
                if (env.OS === 'darwin') {
                    SysTrayWhenStarted = 1
                    SysTrayEnabled = 1
                } else {
                    SysTrayWhenStarted = 0
                    SysTrayEnabled = 0
                }
                HideWindowOnStartOverride = 1
                NeverShowWindow = 1
                NoTrayMenu = 1
            }

            configOverride.push({
                configName: 'globalConfig',
                sectionName: 'BasicWindow',
                propertyName: 'SysTrayWhenStarted',
                type: 'bool',
                value: SysTrayWhenStarted,
            })
            configOverride.push({
                configName: 'globalConfig',
                sectionName: 'BasicWindow',
                propertyName: 'SysTrayEnabled',
                type: 'bool',
                value: SysTrayEnabled,
            })
            configOverride.push({
                configName: 'globalConfig',
                sectionName: 'BasicWindow',
                propertyName: 'HideWindowOnStartOverride',
                type: 'bool',
                value: HideWindowOnStartOverride,
            })
            configOverride.push({
                configName: 'globalConfig',
                sectionName: 'BasicWindow',
                propertyName: 'NeverShowWindow',
                type: 'bool',
                value: NeverShowWindow,
            })
            configOverride.push({
                configName: 'globalConfig',
                sectionName: 'BasicWindow',
                propertyName: 'NoTrayMenu',
                type: 'bool',
                value: NoTrayMenu,
            })
        }

        if (baseCanvasDim) {
            // the dimensions of the OBS canvas
            configOverride.push({
                configName: 'basicConfig',
                sectionName: 'Video',
                propertyName: 'BaseCX',
                type: 'uint',
                value: baseCanvasDim[WIDTH],
            })
            configOverride.push({
                configName: 'basicConfig',
                sectionName: 'Video',
                propertyName: 'BaseCY',
                type: 'uint',
                value: baseCanvasDim[HEIGHT],
            })
        }

        if (videoOutputDim) {
            // the dimension of the video output
            configOverride.push({
                configName: 'basicConfig',
                sectionName: 'Video',
                propertyName: 'OutputCX',
                type: 'uint',
                value: videoOutputDim[WIDTH],
            })
            configOverride.push({
                configName: 'basicConfig',
                sectionName: 'Video',
                propertyName: 'OutputCY',
                type: 'uint',
                value: videoOutputDim[HEIGHT],
            })
        }

        return configOverride
    }

    function makeConfigFromDesktopSource(
        desktopSource: DesktopCapturerSourceExt,
        options: { frameRate?: number; screenWidth?: number }
    ) {
        if (desktopSource && desktopSource.screenIndex != null) {
            const { screenWidth, screenHeight } = desktopSource
            const scaleFactor = desktopSource.scaleFactor || 1

            let videoOutputDim: [number, number] | null = null
            let baseCanvasDim: [number, number] | null = null

            if (screenWidth && screenHeight) {
                if (options.screenWidth) {
                    const scaledScreenWidth = options.screenWidth
                    // calculate scaledScreenHeight while keeping the aspect ratio wrt scaledScreenWidth.
                    const scaledScreenHeight = Math.floor((screenHeight * scaledScreenWidth) / screenWidth)
                    videoOutputDim = [scaledScreenWidth, scaledScreenHeight]
                } else {
                    videoOutputDim = [screenWidth, screenHeight]
                }
            }

            if (screenWidth && screenHeight) {
                baseCanvasDim = [screenWidth * scaleFactor, screenHeight * scaleFactor]
            }

            const screenIndex = desktopSource.screenIndex || 0
            const frameRate = options.frameRate || 30
            const obsConfig = makeConfig({
                screenIndex: screenIndex,
                frameRate: frameRate,
                baseCanvasDim: baseCanvasDim,
                videoOutputDim: videoOutputDim,
            })
            return obsConfig
        } else {
            return null
        }
    }

    return {
        launchProcess,
        killProcess,
        isAvailable,

        makeConfig,
        makeConfigFromDesktopSource,
    }
}
